// ============================================
// CHERCHER UN CLIENT : COMPTES SAV ET BOUTIQUE
// ============================================
// Décision de Nathan du 16/10/2026 : le personnel cherche n'importe quel
// client de klygroupe.com pour lui ouvrir ou lui rattacher un dossier. La
// recherche interroge EN PARALLÈLE les comptes SAV (GET /admin/users) et la
// boutique (GET /admin/clients-boutique) ; la liste est fusionnée, chaque
// compte SAV n'y figure qu'une fois.
//
// Choisir une ligne ne fait que la SÉLECTIONNER : rien n'est créé tant que la
// fenêtre n'est pas validée (« Créer le dossier », « Rattacher »). À la
// validation, toute ligne venue de la boutique passe par POST
// /admin/clients-boutique/:id/compte-sav (idempotent : retrouve ou crée le
// compte SAV, avec les règles de la connexion du client) et le dossier
// reçoit le compte RENDU par le serveur, jamais l'identifiant annoncé dans
// la liste.

import { useEffect, useMemo, useState } from 'react';
import { AdminApi, ApiError, type CompteBoutique } from '../../services/api';
import type { User } from '../../types';

/** Une ligne de la liste fusionnée. */
export interface ResultatClient {
  /** Clé stable : « sav:<id> » ou « boutique:<id_customer> ». */
  cle: string;
  /** Compte SAV de la ligne (affichage, dédoublonnage) ; null = aucun compte SAV connu. */
  compteSavId: string | null;
  /**
   * Compte klygroupe.com d'où vient la ligne (trouvée par la boutique
   * seulement) : à la validation, le compte SAV est demandé au serveur
   * (POST compte-sav). null pour une ligne trouvée parmi les comptes SAV.
   */
  boutiqueId: number | null;
  /** En gras : la société, sinon le nom du compte. */
  titre: string;
  /** La personne (prénom nom), si elle diffère du titre. */
  personne: string | null;
  email: string | null;
  phone: string | null;
  /** Numéro du compte klygroupe.com (id_customer). */
  prestashopId: number | null;
  /** Code client SAGE. */
  customerCode: string | null;
}

/**
 * Le client sélectionné dans une fenêtre. Tant que `boutiqueId` est posé, le
 * compte SAV n'est pas encore établi : assurerCompteSav le demande au
 * serveur à la validation.
 */
export interface ClientChoisi {
  /** Compte SAV à utiliser ; null tant que le serveur ne l'a pas rendu. */
  id: string | null;
  /** Compte klygroupe.com à faire valider par le serveur (POST compte-sav). */
  boutiqueId: number | null;
  /** Le serveur a annoncé un compte SAV existant pour ce compte de la boutique. */
  compteSavAnnonce: boolean;
  displayName: string;
  email: string | null;
  phone: string | null;
  customerCode: string | null;
}

/** Client dont le compte SAV est établi. */
export type ClientEtabli = ClientChoisi & { id: string; boutiqueId: null };

const personneDe = (b: CompteBoutique): string => [b.prenom, b.nom].map(s => (s || '').trim()).filter(Boolean).join(' ');

const differente = (a: string | null | undefined, titre: string): string | null =>
  a && a.trim() && a.trim().toLowerCase() !== titre.trim().toLowerCase() ? a.trim() : null;

/**
 * Fusionne les comptes SAV et les comptes de la boutique. Un compte de la
 * boutique ne se fond dans la ligne d'un compte SAV que si le SERVEUR a
 * annoncé ce lien (compteSav : lien sûr, mêmes règles que la connexion du
 * client) ; il n'apparaît alors qu'une fois, même si plusieurs comptes de la
 * boutique (salariés d'une même société) mènent au même compte SAV. Ordre :
 * comptes SAV trouvés, puis comptes SAV trouvés seulement par la boutique,
 * puis comptes de la boutique sans compte SAV.
 */
export function fusionnerResultats(sav: User[], boutique: CompteBoutique[]): ResultatClient[] {
  const resultats: ResultatClient[] = [];
  const vus = new Set<string>();
  const parCompteSav = new Map<string, CompteBoutique>();
  for (const b of boutique) {
    if (b.compteSav && !parCompteSav.has(b.compteSav.id)) parCompteSav.set(b.compteSav.id, b);
  }

  for (const u of sav) {
    if (vus.has(u.id)) continue;
    vus.add(u.id);
    const b = parCompteSav.get(u.id);
    resultats.push({
      cle: `sav:${u.id}`,
      compteSavId: u.id,
      boutiqueId: null,
      titre: u.displayName,
      personne: b ? differente(personneDe(b), u.displayName) : null,
      email: u.email || b?.email || null,
      phone: u.phone || null,
      prestashopId: u.prestashopCustomerId ?? b?.prestashopId ?? null,
      customerCode: u.customerCode ?? b?.customerCode ?? null,
    });
  }

  for (const b of boutique) {
    if (!b.compteSav || vus.has(b.compteSav.id)) continue;
    vus.add(b.compteSav.id);
    resultats.push({
      cle: `sav:${b.compteSav.id}`,
      compteSavId: b.compteSav.id,
      boutiqueId: b.prestashopId,
      titre: b.compteSav.displayName,
      personne: differente(personneDe(b), b.compteSav.displayName),
      email: b.compteSav.email || b.email,
      phone: null,
      prestashopId: b.prestashopId,
      customerCode: b.customerCode,
    });
  }

  for (const b of boutique) {
    if (b.compteSav) continue;
    const personne = personneDe(b);
    const titre = b.societe?.trim() || personne || b.email;
    resultats.push({
      cle: `boutique:${b.prestashopId}`,
      compteSavId: null,
      boutiqueId: b.prestashopId,
      titre,
      personne: differente(personne, titre),
      email: b.email,
      phone: null,
      prestashopId: b.prestashopId,
      customerCode: b.customerCode,
    });
  }
  return resultats;
}

/** Sélection d'une ligne : rien n'est créé ici. */
export function versClientChoisi(r: ResultatClient): ClientChoisi {
  return {
    id: r.boutiqueId != null ? null : r.compteSavId,
    boutiqueId: r.boutiqueId,
    compteSavAnnonce: r.boutiqueId != null && !!r.compteSavId,
    displayName: r.titre,
    email: r.email,
    phone: r.phone,
    customerCode: r.customerCode,
  };
}

/** Mention sous un client sélectionné venu de la boutique, sinon null. */
export function noteValidation(c: ClientChoisi): string | null {
  if (c.boutiqueId == null) return null;
  return c.compteSavAnnonce
    ? `Compte klygroupe.com n° ${c.boutiqueId} : son compte SAV sera vérifié à la validation.`
    : `Compte klygroupe.com n° ${c.boutiqueId} : le compte SAV sera créé à la validation.`;
}

/**
 * Le compte SAV du client sélectionné, à la VALIDATION de la fenêtre. Venu
 * de la boutique : demandé au serveur (POST compte-sav, idempotent, qui relit
 * la boutique et refait les contrôles de la connexion) ; c'est le compte
 * rendu qui sert, jamais celui annoncé dans la liste.
 */
export async function assurerCompteSav(c: ClientChoisi): Promise<ClientEtabli> {
  if (c.boutiqueId != null) {
    const r = await AdminApi.creerCompteSavBoutique(c.boutiqueId);
    return {
      id: r.id, boutiqueId: null, compteSavAnnonce: false, displayName: r.displayName,
      email: r.email ?? c.email, phone: c.phone, customerCode: r.customerCode ?? c.customerCode,
    };
  }
  if (!c.id) throw new Error('Client sans compte SAV.');
  return { ...c, id: c.id, boutiqueId: null };
}

/** Message après l'échec de la création d'un compte SAV. */
export function messageErreurCompteSav(e: unknown): string {
  if (e instanceof ApiError && (e.statusCode === 400 || e.statusCode === 404 || e.statusCode === 409) && e.message) return e.message;
  if (e instanceof ApiError && e.statusCode === 503) return 'La boutique klygroupe.com est injoignable : le compte SAV de ce client ne peut pas être créé pour le moment.';
  if (e instanceof ApiError && e.statusCode === 403) return "Vous n'avez pas le droit de créer ce compte.";
  return e instanceof Error && e.message ? e.message : 'La création du compte SAV a échoué.';
}

/** Message discret quand la boutique ne répond pas : la recherche SAV continue. */
export const BOUTIQUE_INDISPONIBLE = 'Recherche dans klygroupe.com indisponible pour le moment : seuls les comptes SAV sont proposés.';

/** Longueurs de recherche admises par la boutique (au-delà, comptes SAV seulement). */
export const RECHERCHE_MIN = 2;
export const RECHERCHE_BOUTIQUE_MAX = 100;

export interface EtatRechercheClients {
  resultats: ResultatClient[];
  /** Au moins une des deux recherches est en cours. */
  enCours: boolean;
  /** La recherche dans la boutique est en cours (souvent la plus lente). */
  enCoursBoutique: boolean;
  /** Message discret si la boutique n'a pas répondu, sinon null. */
  boutiqueIndisponible: string | null;
}

/** Recherche remplacée (429 doux du serveur) : ni résultat ni message. */
const estRemplacee = (e: unknown): boolean => e instanceof ApiError && e.statusCode === 429;

/**
 * Recherche en parallèle, 300 ms après la frappe, à partir de 2 caractères.
 * Les comptes SAV s'affichent dès qu'ils arrivent, la boutique (jusqu'à 5 s)
 * complète la liste ensuite. Une frappe nouvelle ANNULE la requête précédente
 * à la boutique (AbortController) ; le serveur n'en traite de plus qu'une à
 * la fois par utilisateur. `actif` à false (client déjà choisi) : rien n'est
 * cherché.
 */
export function useRechercheClients(recherche: string, actif = true, avecComptesSav = true): EtatRechercheClients {
  const [sav, setSav] = useState<User[]>([]);
  const [boutique, setBoutique] = useState<CompteBoutique[]>([]);
  const [enCoursSav, setEnCoursSav] = useState(false);
  const [enCoursBoutique, setEnCoursBoutique] = useState(false);
  const [boutiqueIndisponible, setBoutiqueIndisponible] = useState<string | null>(null);

  useEffect(() => {
    const q = recherche.trim();
    if (!actif || q.length < RECHERCHE_MIN) {
      setSav([]); setBoutique([]); setEnCoursSav(false); setEnCoursBoutique(false); setBoutiqueIndisponible(null);
      return;
    }
    let vivant = true;
    const abandon = new AbortController();
    const minuteur = setTimeout(() => {
      if (avecComptesSav) {
        setEnCoursSav(true);
        // Promise.resolve().then : une levée immédiate devient un échec traité.
        Promise.resolve()
          .then(() => AdminApi.getUsers({ role: 'CUSTOMER', search: q, limit: 10 }))
          .then(comptes => { if (vivant) setSav(Array.isArray(comptes) ? comptes : []); })
          .catch(() => { if (vivant) setSav([]); })
          .finally(() => { if (vivant) setEnCoursSav(false); });
      }
      if (q.length > RECHERCHE_BOUTIQUE_MAX) {
        setBoutique([]);
        setBoutiqueIndisponible(null);
        return;
      }
      setEnCoursBoutique(true);
      Promise.resolve()
        .then(() => AdminApi.rechercherClientsBoutique(q, { signal: abandon.signal }))
        .then(comptes => { if (vivant) { setBoutique(Array.isArray(comptes) ? comptes : []); setBoutiqueIndisponible(null); } })
        .catch((e) => {
          // Annulée par la frappe suivante, ou remplacée côté serveur : rien à dire.
          if (!vivant || abandon.signal.aborted || estRemplacee(e)) return;
          setBoutique([]);
          setBoutiqueIndisponible(BOUTIQUE_INDISPONIBLE);
        })
        .finally(() => { if (vivant) setEnCoursBoutique(false); });
    }, 300);
    return () => { vivant = false; clearTimeout(minuteur); abandon.abort(); };
  }, [recherche, actif, avecComptesSav]);

  const resultats = useMemo(() => fusionnerResultats(sav, boutique), [sav, boutique]);
  return { resultats, enCours: enCoursSav || enCoursBoutique, enCoursBoutique, boutiqueIndisponible };
}
